import * as Sentry from '@sentry/browser';
import { analytics } from '@play-co/gcinstant';
import { configureStore } from '@reduxjs/toolkit';
import {
  ClientReplicant,
  parseReplicantPlatform,
  createClientReplicant,
  ReplicantError,
  ReplicantFromConfig,
  createWebPlayableReplicant,
} from '@play-co/replicant';
import reducers from 'src/state/reducers';
import type { State } from 'src/state';
import replicantConfig from 'src/replicant/config';
import type { State as ReplicantState } from 'src/replicant/State';
import { update } from 'src/state/user';
import { onError } from './lib/errors';
import { PerformanceMetricsTracker } from './lib/PerformanceAnalytics';
import type { Locale } from './replicant/ruleset/locale';
import config from 'src/replicant/config';
import { addAppVisibilityListener, waitForItPromise } from './lib/utils';
import { sentryForRedux } from './lib/sentryForRedux';
import { payloadToData } from './lib/payloadToData';
import { resetAllSessionStats } from './game/stats/stats.modifiers';
import { getManualAssignAbTestArgs } from './game/ab/ab.utils';
import { consumeGuestModeLocalState } from './game/guest/guest.sequence';
import { GCInstant } from './lib/gcinstant';
import { devSettings } from './lib/settings';
import { captureGenericError } from './lib/sentry';
import {
  isRecoverableAuthError,
  isLiffReauthAvailable,
  attemptAuthRecovery,
  checkAndTrackRecoverySuccess,
} from './lib/authRecovery';

type Listener = (state: State) => void;

// Dev mode settings
// WARNING: immutableCheck and serializableCheck will be switched to false on production
// See: https://github.com/reduxjs/redux-toolkit/blob/659ff141edf32f68a5b67072d8c161625b369024/src/getDefaultMiddleware.ts#L57
const storeConfig = {
  devTools: true,
  immutableCheck: true,
  serializableCheck: true,
};

// Three seconds is just enough to capture an individual spin -> consume cycle.
// Also, do not batch requests in local dev when FB is simulated.
const BATCHING_MAX_TIME = process.env.SIMULATED ? 0 : 3000;

// TODO A/B test this (maybe...)
// Check for messages this often. We want to feel pretty realtime.
const HEARTBEAT_INTERVAL = 10000; // target: 10s

// TODO A/B test this
// Load friend states this often. We don't want stale friend data.
const STATE_LOAD_INTERVAL = 30000; // target: 10s

// For happy fun dev times!
const simulatedLatencyOffline = 0;

class StateObserver {
  private store = configureStore({
    reducer: reducers,
    devTools: storeConfig.devTools,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: storeConfig.immutableCheck,
        serializableCheck: storeConfig.serializableCheck,
      }).concat(sentryForRedux),
  });

  public replicant!: ClientReplicant<ReplicantFromConfig<typeof config>>;

  public invoke!: StateObserver['replicant']['invoke'];

  public performanceTracker = new PerformanceMetricsTracker('user/update');
  public listenerTracker = new PerformanceMetricsTracker('SO/listeners');
  public replicantClientPromise = new Promise<ClientReplicant<any>>(
    (resolve) => {
      this.resolveReplicantClient = resolve;
    },
  );

  private listeners!: Array<Listener>;
  /**
   * Detach handlers. Held weakly where WeakRef exists, so registering is
   * never the reason an {@link Emitter} - and the view tree its selector
   * closes over - outlives the screen that created it. Chrome < 84 (an
   * un-updated Android 8 WebView) has no WeakRef; fall back to strong
   * references there rather than crash on the first Emitter.
   */
  private onDetachListenersHandlers: Array<WeakRef<() => void> | (() => void)> =
    [];
  private useWeakRef = typeof WeakRef !== 'undefined';
  private detachHandlerPruneAt = 64;
  private resolveReplicantClient!: (
    replicantClient: ClientReplicant<any>,
  ) => void;

  private inBackground = false;
  private backgroundPromise: Promise<void> = Promise.resolve();
  private backgroundResolver: (() => void) | undefined;

  public initLocal() {
    if (this.listeners) {
      console.warn('StateObserver.initLocal: Already initialized.');
      return;
    }
    this.listeners = [];
    this.store.subscribe(() =>
      this.listeners.forEach((x) => x(this.getState())),
    );
  }

  public async init(sendActionAnalyticsWithClient = true) {
    this.initLocal();

    // Create the appropriate replicant based on environment.
    this.replicant = await this.createReplicant(sendActionAnalyticsWithClient);

    // Track successful recovery after an auth recovery redirect.
    // checkAndTrackRecoverySuccess has an internal try/catch because
    // onError isn't registered yet and a tracking failure should never
    // crash app init after a successful login.
    checkAndTrackRecoverySuccess();

    void this.setupDebugging();

    this.replicant.setOnError(onError);

    this.replicant.setActionPreInvokeCallback((action, args) => {
      Sentry.addBreadcrumb({
        category: 'replicant-action',
        message: action,
        level: 'info',
        data: payloadToData(args) ?? {},
      });
    });

    this.replicant.setActionFailedCallback((action, args) => {
      Sentry.addBreadcrumb({
        category: 'replicant-action-failed',
        message: action,
        level: 'info',
        data: payloadToData(args) ?? {},
      });
    });

    this.invoke = this.replicant.invoke;

    this.attachStoreToReplicant();

    this.resolveReplicantClient(this.replicant);

    addAppVisibilityListener((appState: DocumentVisibilityState) => {
      this.inBackground = appState === 'hidden';

      if (this.inBackground) {
        this.backgroundPromise = new Promise((resolve) => {
          this.backgroundResolver = resolve;
        });
      } else {
        this.backgroundResolver?.();
        this.backgroundResolver = undefined;
        this.backgroundPromise = Promise.resolve();
      }
    });
  }

  private async createReplicant(sendActionAnalyticsWithClient: boolean) {
    // Common configuration for all replicant types
    const commonConfig: typeof replicantConfig = {
      ...replicantConfig,
      sendActionAnalyticsWithClient,
    };

    // Prefetch the encoded payload item from Replicant's KV store
    // when logging in, instead of having to fetch it with a
    // separate request later on.
    const prefetchKey = GCInstant.entryData?.$key;

    // Common options shared between replicant types
    const commonOptions = {
      onLoginActionArgs: getManualAssignAbTestArgs(),
      batchingMaxTime: BATCHING_MAX_TIME,
      checkForMessagesInterval: HEARTBEAT_INTERVAL,
      refreshFriendsStatesInterval: STATE_LOAD_INTERVAL,
      kvStore: { prefetchKeys: prefetchKey ? [prefetchKey] : undefined },
      endpoint: process.env.REPLICANT_ENDPOINT,
      platform: parseReplicantPlatform(process.env.PLATFORM) ?? 'mock',
    };

    // If LINE_GUEST is enabled for legacy quest mode builds
    // Or if GCInstant is in guest mode for new unified guest build
    const isGuestMode = GCInstant.platformID === 'line-guest'; // New unified guest mode

    // For guest mode, we create a web playable replicant
    // Web playable replicant allow us to have user state on server  not locally
    // We save on localstorage unique user ID
    // User ID for web playable starts with web_[UID]
    if (isGuestMode) {
      try {
        return createWebPlayableReplicant(commonConfig, {
          ...commonOptions,
          onLoginActionArgs: {
            ...commonOptions.onLoginActionArgs,
            offlineState: consumeGuestModeLocalState(),
          },
          // Platform is ALWAYS web for guest mode
          platform: 'web',
        });
      } catch (error) {
        captureGenericError('Failed to create guest mode replicant', {
          error,
        });
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        throw new Error(
          `Failed to create guest mode replicant: ${errorMessage}`,
        );
      }
    }

    // Otherwise, use the default client replicant.
    try {
      return await createClientReplicant(commonConfig, GCInstant.playerID, {
        ...commonOptions,
        // Online opts
        signature: GCInstant.playerSignature,
        obtainSignature: () => GCInstant.refreshPlayerSignature(),
        // Offline opts
        latency: simulatedLatencyOffline,
        storageKeyPrefix: 'default',
        devOpts: {
          errorReporter: {
            addBreadcrumb: (breadcrumb) => Sentry.addBreadcrumb(breadcrumb),
            captureException: (error, reqContext, extraContext) => {
              Sentry.captureException(error, {
                extra: {
                  ...reqContext?.context,
                  ...extraContext,
                  ...(error instanceof ReplicantError ? error.extras : {}),
                },
                tags: reqContext?.tags,
              });
            },
          },
        },
      });
    } catch (error) {
      // Handle auth errors during initial login by forcing LIFF re-authentication.
      // This happens when the LIFF SDK returns a stale cached access token
      // (invalid_signature) or an expired cached ID token (token_expired). The onError
      // handler isn't registered yet at this point, so we must handle it here.
      if (isRecoverableAuthError(error) && isLiffReauthAvailable()) {
        const redirected = await attemptAuthRecovery();
        if (redirected) {
          // Wait for the redirect to happen before throwing the error.
          await waitForItPromise(7000);
          throw error;
        }
        Sentry.addBreadcrumb({
          category: 'auth-recovery',
          message: 'Recovery did not redirect, re-throwing auth error',
          level: 'error',
        });
      }

      throw error;
    }
  }

  public addListener(listener: Listener, opts?: { noInit?: boolean }) {
    if (!this.listeners) {
      throw new Error('StateObserver.addListener Error: Not initialized.');
    }

    if (this.listeners.includes(listener)) {
      throw new Error(
        'this.removeListener Error: Listener already subscribed.',
      );
    }

    this.listeners.push(listener);

    opts?.noInit || listener(this.getState());

    return listener;
  }

  public removeListener(listener: Listener) {
    if (!this.listeners) {
      throw new Error('StateObserver.removeListener Error: Not initialized.');
    }

    const index = this.listeners.indexOf(listener);

    if (index < 0) {
      throw new Error('this.removeListener Error: Listener not found.');
    }

    this.listeners.splice(index, 1);
  }

  public detachListeners() {
    const refs = this.onDetachListenersHandlers;
    const live: Array<WeakRef<() => void> | (() => void)> = [];

    for (const ref of refs) {
      const handler = this.derefHandler(ref);
      if (!handler) continue;

      live.push(ref);
      handler();
    }

    this.onDetachListenersHandlers = live;

    // Copy first: removeListener splices the array being iterated.
    for (const listener of [...this.listeners]) {
      this.removeListener(listener);
    }
  }

  //

  public getState() {
    return this.store.getState() as State;
  }

  public readonly dispatch: StateObserver['store']['dispatch'] =
    this.store.dispatch.bind(this.store);

  public now(): number {
    return this.replicant.now();
  }

  public getSessionData(): { sessionId: string; appVersion: string } {
    return this.replicant.getChatbotSessionData();
  }

  public onDetachListeners(handler: () => void) {
    // Drop refs to collected handlers. Amortised: the list stays within
    // max(64, twice the live count at the last prune).
    if (this.onDetachListenersHandlers.length >= this.detachHandlerPruneAt) {
      this.onDetachListenersHandlers = this.onDetachListenersHandlers.filter(
        (ref) => this.derefHandler(ref),
      );
      this.detachHandlerPruneAt = Math.max(
        64,
        this.onDetachListenersHandlers.length * 2,
      );
    }

    this.onDetachListenersHandlers.push(
      this.useWeakRef ? new WeakRef(handler) : handler,
    );
  }

  private derefHandler(
    ref: WeakRef<() => void> | (() => void),
  ): (() => void) | undefined {
    return this.useWeakRef
      ? (ref as WeakRef<() => void>).deref()
      : (ref as () => void);
  }

  public resetAppState() {
    // Reset session stats
    resetAllSessionStats();

    // Reset redux state
    this.store.dispatch({
      type: 'RESET_APP',
      payload: this.store.getState().user,
    });
  }

  public async refreshUserState(refreshSignature: boolean): Promise<boolean> {
    // Disable redux state updates until `onLogin` is done.
    this.detachStoreFromReplicant();

    try {
      await this.replicant.refresh(
        refreshSignature ? GCInstant.playerSignature : undefined,
      );

      // End any interrupted puzzle before login applies abandonment penalties.
      await this.invoke.abortMatch2Puzzle();

      // Call onLogin again, but without args, so the manual AB tests are not assigned again.
      await this.invoke.onLogin(undefined);
      return true;
    } catch (err) {
      // This error will get forwaded to `onError`
      return false;
    } finally {
      // Re-enable updates.
      this.attachStoreToReplicant();
    }
  }

  public async refreshMessages() {
    if (this.replicant) {
      await this.replicant.flush();
      await this.replicant.checkForMessages();
    }
  }

  /**
   * Flush replicant requests and analytics events.
   */
  public async flush() {
    if (this.replicant) {
      await this.replicant.checkForMessages().catch((e) => {
        captureGenericError('Error replicant checkForMessages:', e);
        console.error(e);
      });
      await Promise.all([this.replicant.flush(), analytics.flush()]);
    } else {
      await analytics.flush();
    }
  }

  private attachStoreToReplicant() {
    this.onStateChanged(this.replicant.state);
    this.replicant.onStateChanged(this.onStateChanged);
  }

  private detachStoreFromReplicant() {
    this.replicant.removeStateChangedHandler(this.onStateChanged);
  }

  private onStateChanged = (state: ReplicantState) => {
    // Measure the listeners before dispatching.
    this.listenerTracker.measure(this.listeners.length);

    const start = performance.now();
    this.store.dispatch(update(state));
    const end = performance.now();
    this.performanceTracker.measure(end - start);
  };

  /**
   * Get current user locale
   */
  public getLocale(): Locale {
    return this.getState().user.settings.locale as Locale; // TODO: remove type coercion to improve type safety
  }

  public isInBackground() {
    return this.inBackground;
  }

  public waitForForeground() {
    return this.backgroundPromise;
  }

  private async setupDebugging() {
    // Only in development.
    if (!process.env.IS_DEVELOPMENT) return;
    // Not enabled in unit tests.
    if (process.env.APP_VERSION === 'test') return;

    // Disable debug panel if it disabled in dev settings.
    if (!devSettings.get('debugConsole')) return;

    // Import dynamically so it doesn't get bundled in production.
    const { configureDebugPanel, setDebugPanelPosition } =
      await import('@play-co/debug-panel');

    const positions = [
      'top-left',
      'top-right',
      'bottom-left',
      'bottom-right',
    ] as const;
    const move = (position: (typeof positions)[number]) => {
      setDebugPanelPosition(position);
      localStorage.setItem('debugPanelPosition', position);
    };

    configureDebugPanel({
      replicant: this.replicant as any,
      ui: {
        'Panel Position': Object.fromEntries(
          positions.map((position) => [position, () => move(position)]),
        ),
      },
    });

    // Restore the saved corner (default top-left, to keep clear of the HUD).
    // configureDebugPanel creates its button asynchronously and ignores a
    // position set before the button exists, so wait for it to appear first.
    const saved = localStorage.getItem('debugPanelPosition');
    const restore = () => {
      if (!document.getElementById('debug-panel-start')) {
        requestAnimationFrame(restore);
        return;
      }
      setDebugPanelPosition(
        (positions as readonly string[]).includes(saved as string)
          ? (saved as (typeof positions)[number])
          : 'top-left',
      );
    };
    restore();
  }
}

const instance = new StateObserver();

export default instance;

// Convenience function for getting current user state
export const user = () => instance.getState().user;

// Convenience function for getting current server time
export const now = () => instance.now();

// Convenience function for getting current user locale
export const locale = () => instance.getLocale();

// Expose singleton StateObserver to window in development mode
if (process.env.IS_DEVELOPMENT) {
  (window as any).StateObserver = instance;
  (window as any).GCInstant = GCInstant;
}
